import { useEffect, useId, useMemo } from 'react';
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMountedState, useUpdateEffect } from 'react-use';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, Grid, MenuItem, Skeleton, Stack, Typography } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { isEmpty, trim } from 'lodash';

import {
  CLASSIFICATION_ATC,
  CLASSIFICATION_CATEGORY,
  CLASSIFICATION_FORM,
  CLASSIFICATION_MU,
  type Classification,
} from '@/api/domains/classifications.types';
import { DrugsApi, type DrugFormData } from '@/api/domains/drugs.api';
import { DrugsQueryKeys } from '@/api/domains/drugs.query-keys';
import type { Drug, Maker } from '@/api/domains/drugs.types';
import type { Inn } from '@/api/domains/inn.type';
import { useGlobalErrorStore } from '@/components/dialogs';
import {
  CustomFormLabel,
  Form,
  RHFAutocomplete,
  RHFCheckbox,
  RHFIntegerTextField,
  RHFTextField,
} from '@/components/form-controls';
import { RHFIkpuField } from '@/components/form-controls/IkpuField/RHFIkpuField';
import { RHFClassificationField } from '@/components/form-controls/RHFClassificationField';
import { RHFInnField } from '@/components/form-controls/RHFInnField';
import { RHFMakerField } from '@/components/form-controls/RHFMakerField';
import { ModalBodyContent, ModalSidebarContent } from '@/components/Modal';
import { ModalBody } from '@/components/Modal/ModalBody2';
import { nomenclatureCreateEvent } from '@/components/nomenclature/events';
import { NomenclatureModalToolbar } from '@/components/nomenclature/modals/NomenclatureModalToolbar';
import {
  selectNomenclatureStoreProductNameToCreate,
  selectPreferredTab,
  useNomenclatureContextWindows,
  useNomenclatureStore,
  useNomenclatureStoreActions,
} from '@/components/nomenclature/useNomenclatureStore';
import { ModalBodyContainer } from '@/pages/settings/pharmacy-settings/modals/shared/ContractorsModalBodyContainer';
import { CLASSIFICATION_ATC_NODE_ID, CLASSIFICATION_FORM_NODE_ID } from '@/shared/constants/classification.constants';
import type { RecordStatusId } from '@/shared/constants/record-status.constants';
import { RECORD_STATUS_NEW } from '@/shared/constants/record-status.constants';
import { useClassifications } from '@/shared/hooks/useClassifications';
import { useInfo } from '@/shared/hooks/useInfo';
import { handleAxiosErrorWithStatus } from '@/shared/lib/axios/error-handling';
import { Yup } from '@/shared/lib/yup';
import type { AfterSubmit } from '@/shared/types';

import { NomenclatureConfirmationCreateDialog } from '../dialogs/NomenclatureConfirmationCreateDialog';
import { NomenclatureConfirmationEditDialog } from '../dialogs/NomenclatureConfirmationEditDialog';
import { prepareNomenclatureBarcodes } from '../helpers';
import { NomenclatureFormBarcodes } from './NomenclatureFormBarcodes';
import { NomenclatureModalSidebar } from './NomenclatureModalSidebar';
import type { NomenclatureModalTab } from './NomenclatureModalTabbar';
import type { NomenclatureModalVariant } from './types';

// ----------------------------------------------------------------------

const CURRENT_TAB_NAME: NomenclatureModalTab = 'general';

export type NomenclatureFormProps = {
  onClose: VoidFunction;
} & {
  variant: NomenclatureModalVariant;
  drug?: Drug | null;
};

export type ToString<TData extends object> = Record<keyof TData, string>;

type FormValues = AfterSubmit &
  ToString<
    Omit<
      DrugFormData,
      | 'category_id'
      | 'measure_unit_id'
      | 'class_id'
      | 'is_receipt'
      | 'maker_name'
      | 'maker_id'
      | 'country_name'
      | 'description'
      | 'inn_id'
      | 'record_status_id'
      | 'has_marks'
      | 'package_code'
      | 'package_name'
    >
  > & {
    inn: Inn | null;
    maker: Maker | null;
    category: Classification | null;
    measureUnit: Classification | null;
    classification: Classification | null;
    is_receipt: boolean;
    isSelectedCategoryOfTypeMedicines: boolean;
    record_status_id: RecordStatusId;
    licenseHolder: Maker | null;
    releaseForm: Classification | null;
    barcodes: Array<{ value: string }>;
    package_code: string;
    has_marks: boolean;
  };

type Parsed = { packageCode: number; packageName: string };

function parsePackageCodeAndName(str: string): Parsed {
  const separator = ' - ';
  const i = str.indexOf(separator);
  if (i === -1) return { packageCode: Number(trim(str)) || 0, packageName: '' };
  return {
    packageCode: Number(trim(str.slice(0, i))) || 0,
    packageName: str.slice(i + separator.length) || '',
  };
}

// ----------------------------------------------------------------------

const createFormSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    name: Yup.string().trim().required(t('nomenclature-page:modals.form.validation.required')),
    category: Yup.object()
      .shape({ id: Yup.number().required() })
      .nullable()
      .required(t('nomenclature-page:modals.form.validation.required')),
    measureUnit: Yup.object().shape({ id: Yup.number().required() }).nullable(),
    maker: Yup.object()
      .shape({ id: Yup.number().required() })
      .nullable()
      .required(t('nomenclature-page:modals.form.validation.required')),
    vat_id: Yup.string().trim(),
    inn: Yup.object()
      .shape({ id: Yup.number().required() })
      .nullable()
      .when('isSelectedCategoryOfTypeMedicines', {
        is: true,
        then: schema => schema.required(t('nomenclature-page:modals.form.validation.required')),
      }),
    form: Yup.string().trim(),
    number: Yup.string().trim(),
    classification: Yup.object()
      .shape({ id: Yup.number() })
      .nullable()
      .when('isSelectedCategoryOfTypeMedicines', {
        is: true,
        then: schema => schema.required(t('nomenclature-page:modals.form.validation.required')),
      }),
    ikpu: Yup.string()
      .trim()
      .nullable()
      .test('is-valid-ikpu', t('nomenclature-page:modals.form.validation.invalid-ikpu'), value => {
        if (!value) return true;
        return /^\d{17}$/.test(value);
      }),
    has_marks: Yup.boolean(),
    package_code: Yup.string().trim(),
    isSelectedCategoryOfTypeMedicines: Yup.bool(),
    licenseHolder: Yup.object()
      .shape({ id: Yup.number().required() })
      .nullable()
      .required(t('nomenclature-page:modals.form.validation.required')),
    releaseForm: Yup.object()
      .shape({ id: Yup.number().required() })
      .nullable()
      .when('isSelectedCategoryOfTypeMedicines', {
        is: true,
        then: schema => schema.required(t('nomenclature-page:modals.form.validation.required')),
      }),
    barcodes: Yup.array().of(
      Yup.object().shape({
        value: Yup.string().trim(),
      }),
    ),
    factory_package: Yup.string().trim(),
    blister_count: Yup.string().trim(),
  });

// ----------------------------------------------------------------------

export const NomenclatureForm = (props: NomenclatureFormProps) => {
  const { drug, onClose, variant } = props;
  const { t } = useTranslation('nomenclature-page');

  const isCreateMode = variant === 'create';
  const isEditMode = variant === 'edit';
  const isEditAfterCreationMode = variant === 'editAfterCreation';
  const isDuplicateMode = variant === 'duplicate';
  const shouldPrefillNomenclatureForm = (isEditMode || isDuplicateMode || isEditAfterCreationMode) && !!drug;
  const isNumberFieldEnabled = drug?.record_status_id === RECORD_STATUS_NEW || isCreateMode;

  const productNameToCreate = useNomenclatureStore(selectNomenclatureStoreProductNameToCreate);
  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    // @ts-expect-error - yupResolver types issue
    resolver: yupResolver(createFormSchema(t)),
    defaultValues: {
      isSelectedCategoryOfTypeMedicines: false,
      is_receipt: shouldPrefillNomenclatureForm ? drug.is_receipt : false,
      name: shouldPrefillNomenclatureForm ? drug.name : (productNameToCreate ?? ''),
      form: shouldPrefillNomenclatureForm ? drug.form : '',
      number: shouldPrefillNomenclatureForm ? String(drug.number) : '',
      ikpu: shouldPrefillNomenclatureForm ? String(drug.ikpu) : '',
      has_marks: shouldPrefillNomenclatureForm ? drug.has_marks : false,
      package_code:
        shouldPrefillNomenclatureForm && drug.package_code ? `${drug.package_code} - ${drug.package_name}` : '',
      vat_id: shouldPrefillNomenclatureForm ? String(drug.vat_id) : '',
      inn: shouldPrefillNomenclatureForm ? drug.inn : null,
      maker: shouldPrefillNomenclatureForm ? drug.maker : null,
      category: shouldPrefillNomenclatureForm ? drug.category : null,
      measureUnit: shouldPrefillNomenclatureForm ? drug.measure_unit : null,
      classification: shouldPrefillNomenclatureForm ? drug.classification : null,
      record_status_id:
        shouldPrefillNomenclatureForm && isDuplicateMode
          ? RECORD_STATUS_NEW
          : !shouldPrefillNomenclatureForm
            ? RECORD_STATUS_NEW
            : drug.record_status_id,
      licenseHolder: shouldPrefillNomenclatureForm ? drug.license_holder : null,
      releaseForm: shouldPrefillNomenclatureForm ? drug.release_form : null,
      barcodes:
        shouldPrefillNomenclatureForm && !isEmpty(drug.barcodes)
          ? prepareNomenclatureBarcodes(drug.barcodes)
          : [{ value: '' }],
      factory_package: shouldPrefillNomenclatureForm ? String(drug.factory_package) : '',
      blister_count: shouldPrefillNomenclatureForm ? String(drug.blister_count) : '',
    },
  });

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    handleSubmit: wrapHandleSubmit,
    formState: { isDirty, submitCount, isSubmitting },
  } = methods;

  // ----------------------------------------------------------------------

  const [maker, category, currentLicenseHolder] = watch(['maker', 'category', 'licenseHolder']);

  // ----------------------------------------------------------------------
  // MAKER
  useEffect(() => {
    if (!currentLicenseHolder) {
      setValue('licenseHolder', maker);
      clearErrors('licenseHolder');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maker]);

  // ----------------------------------------------------------------------
  // DATA

  const { isLoading: isLoadingVats, info: vats } = useInfo({ limit: 50, aliases: ['vat'] });

  const { defaultVat } = useMemo(() => {
    const zeroPercentVat = vats.find(vat => /0/gi.test(vat.name));
    const twelvePercentVat = vats.find(vat => /12/gi.test(vat.name));

    return {
      defaultVat: twelvePercentVat,
      zeroPercentVat,
      twelvePercentVat,
    };
  }, [vats]);

  // ----------------------------------------------------------------------
  // CATEGORY FIELD

  const { isFetching: isFetchingCategories, classifications: categoriesOptions } = useClassifications({
    alias: CLASSIFICATION_CATEGORY,
  });

  const isSelectedCategoryOfTypeMedicines = !!category && /лс/gi.test(category.name);

  useEffect(() => {
    setValue('isSelectedCategoryOfTypeMedicines', isSelectedCategoryOfTypeMedicines);
  }, [isSelectedCategoryOfTypeMedicines, setValue]);

  useUpdateEffect(() => {
    const isSelectedCategoryDifferentFromInitial = category?.id !== drug?.category?.id;
    const shouldResetFieldsDependentOnCategory = isCreateMode || isSelectedCategoryDifferentFromInitial;

    if (shouldResetFieldsDependentOnCategory) {
      setValue('measureUnit', defaultMeasureUnit ?? null);

      if (submitCount > 0) {
        trigger(['classification', 'inn', 'releaseForm']);
      }
    }
  }, [category]);

  // ----------------------------------------------------------------------
  // MEASURE-UNIT FIELD
  const { isFetching: isFetchingMU, classifications: measureUnits } = useClassifications({
    alias: CLASSIFICATION_MU,
  });

  const defaultMeasureUnit = useMemo(
    () => measureUnits.find(measureUnit => /шт/gi.test(measureUnit.name)),
    [measureUnits],
  );

  useEffect(() => {
    if (isCreateMode && defaultMeasureUnit) {
      setValue('measureUnit', defaultMeasureUnit);
    }
  }, [isCreateMode, defaultMeasureUnit, setValue]);

  // ----------------------------------------------------------------------
  // VAT FIELD

  useEffect(() => {
    if (isCreateMode && defaultVat) {
      setValue('vat_id', String(defaultVat.id));
    }
  }, [isCreateMode, defaultVat, setValue]);

  // ----------------------------------------------------------------------

  const preferredTab = useNomenclatureStore(selectPreferredTab);

  useUpdateEffect(() => {
    if (preferredTab === CURRENT_TAB_NAME) {
      return;
    }

    if (isCreateMode || isDuplicateMode) {
      openConfirmationCreateDialog();
    } else {
      if (isDirty) {
        openConfirmationEditDialog();
      } else {
        setActiveTab(preferredTab);
      }
    }
  }, [preferredTab]);

  const isMounted = useMountedState();
  const queryClient = useQueryClient();
  const { setGlobalError } = useGlobalErrorStore();

  const {
    openDuplicateDialog,
    setActiveTab,
    setPreferredTab,
    openConfirmationEditDialog,
    closeConfirmationEditDialog,
    openConfirmationCreateDialog,
    closeConfirmationCreateDialog,
    closeEditModal,
    setSelectedRow,
    setVariant,
  } = useNomenclatureStoreActions();
  const { isConfirmationEditDialogVisible, isConfirmationCreateDialogVisible, isEditModalVisible } =
    useNomenclatureContextWindows();

  const formId = useId();

  const handleSubmit = async (formData: FormValues) => {
    try {
      if (isDuplicateMode && !isDirty) {
        openDuplicateDialog();

        return;
      }

      const {
        is_receipt,

        name,
        vat_id,
        number,
        form,
        ikpu,
        has_marks,
        package_code,
        barcode,
        barcodes,
        blister_count,
        factory_package,
        inn,
        maker,
        category,
        measureUnit,
        classification,
        record_status_id,
        licenseHolder,
        releaseForm,
      } = formData;

      if (!maker || !category || !measureUnit || !licenseHolder) {
        throw new Error(t('modals.form.validation.fill-required-fields'));
      }

      const preparedBarcodes = barcodes.map(barcode => barcode.value).filter(barcode => barcode !== '');

      const { packageCode, packageName } = parsePackageCodeAndName(package_code);

      const preparedFormData: DrugFormData = {
        is_receipt,
        name,
        form,

        number: Number(number),
        blister_count: Number(blister_count),
        factory_package: Number(factory_package),
        barcode: Number(barcode),
        barcodes: preparedBarcodes,
        vat_id: Number(vat_id),
        ikpu,
        package_name: packageName,
        package_code: packageCode,
        has_marks,

        maker_id: maker.id,
        maker_name: maker.name,
        country_name: maker.country,
        category_id: category.id,
        measure_unit_id: measureUnit.id,

        inn_id: inn?.id ?? 0,
        class_id: classification?.id ?? 0,

        record_status_id,

        license_holder_id: licenseHolder.id,
        release_form_id: releaseForm?.id ?? 0,

        url_resources: drug?.url_resources,
      };

      let createdDrug: Drug | null = null;

      if (isEditMode || isEditAfterCreationMode) {
        if (drug && isDirty) {
          const updatedDrug = await DrugsApi.updateDrug(drug.id, preparedFormData);
          queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());

          setSelectedRow(updatedDrug);
        }
      } else {
        createdDrug = await DrugsApi.createDrug(preparedFormData);
        queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());

        setVariant('editAfterCreation');
        setSelectedRow(createdDrug);
      }

      if ((isCreateMode || isDuplicateMode) && preferredTab === CURRENT_TAB_NAME) {
        if (createdDrug) {
          nomenclatureCreateEvent.emit(createdDrug);
        }
      }

      if (isEditAfterCreationMode && preferredTab === CURRENT_TAB_NAME) {
        if (drug) {
          nomenclatureCreateEvent.emit(drug);
        }
      }

      if (isConfirmationCreateDialogVisible) {
        closeConfirmationCreateDialog();
      }

      if (isConfirmationEditDialogVisible) {
        closeConfirmationEditDialog();
      }

      if (preferredTab !== CURRENT_TAB_NAME) {
        setActiveTab(preferredTab);
      }

      if (isDuplicateMode && isEditModalVisible) {
        closeEditModal();
      }

      if (preferredTab === CURRENT_TAB_NAME) {
        if (isDirty && !isCreateMode) {
          await queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());
        }

        onClose();
      }
    } catch (error) {
      setGlobalError(error);
      setPreferredTab('general');

      handleAxiosErrorWithStatus({
        error,
        status: HttpStatusCode.Conflict,
        handler: () => setVariant(variant),
      });

      if (isMounted()) {
        // @ts-expect-error - error object type issue
        setError('afterSubmit', { ...error, message: error.message });
        setPreferredTab(CURRENT_TAB_NAME);
      }
    }
  };

  const handleNomenclatureConfirmationEditDialogClose = () => {
    if (preferredTab === CURRENT_TAB_NAME) {
      onClose();
    } else {
      setActiveTab(preferredTab);
    }
  };

  const handleValidationError = () => {
    setPreferredTab(CURRENT_TAB_NAME);
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <ModalBodyContainer>
          <Stack direction="row" spacing={1} flexGrow={1}>
            <ModalBody
              toolbar={<NomenclatureModalToolbar disabled={!isEditMode} />}
              slotProps={{
                root: {
                  variant: 'outlined',
                },
              }}
            >
              <ModalBodyContent>
                <Form
                  id={formId}
                  onSubmit={wrapHandleSubmit(handleSubmit, handleValidationError)}
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', px: 2 }}
                >
                  <Stack spacing={5}>
                    <Grid container rowSpacing={2}>
                      <Grid item md={12}>
                        <Typography component="p" variant="Title/Bold/medium">
                          {t('modals.form.sections.basic-info')}
                        </Typography>
                      </Grid>
                      <Grid item md={4}>
                        {/* CATEGORY  */}
                        <FormControl fullWidth>
                          <CustomFormLabel isRequired htmlFor="category">
                            {t('modals.form.category')}
                          </CustomFormLabel>
                          <RHFAutocomplete
                            name="category"
                            id="category"
                            loading={isFetchingCategories}
                            loadingText={t('modals.form.ui.loading')}
                            noOptionsText={t('modals.form.ui.no-data')}
                            options={categoriesOptions}
                            getOptionLabel={option =>
                              typeof option === 'string' ? option : `${option.name} - ${option.description}`
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            TextFieldProps={{ variant: 'filled', placeholder: t('modals.form.ui.search') }}
                          />
                        </FormControl>
                        {/* CATEGORY END */}
                      </Grid>
                      <Grid item md={12}>
                        {/* NAME */}
                        <FormControl fullWidth>
                          <CustomFormLabel isRequired htmlFor="name">
                            {t('modals.form.name')}
                          </CustomFormLabel>
                          <RHFTextField fullWidth name="name" id="name" variant="filled" />
                        </FormControl>
                        {/* NAME END */}
                      </Grid>
                      <Grid container item md={12} spacing={2}>
                        <Grid item md={8}>
                          {/* FORM */}
                          <FormControl fullWidth>
                            <CustomFormLabel htmlFor="form">{t('modals.form.form-dosage')}</CustomFormLabel>
                            <RHFTextField fullWidth name="form" id="form" variant="filled" />
                          </FormControl>
                          {/* FORM END */}
                        </Grid>
                        <Grid item md={4}>
                          {/* NUMBER */}
                          <FormControl fullWidth>
                            <CustomFormLabel htmlFor="number">
                              {t('modals.form.ui.quantity-in-package')}
                            </CustomFormLabel>
                            <RHFIntegerTextField
                              disabled={!isNumberFieldEnabled}
                              fullWidth
                              name="number"
                              id="number"
                              variant="filled"
                            />
                          </FormControl>
                          {/* NUMBER END */}
                        </Grid>
                      </Grid>
                      <Grid container item md={12} spacing={2}>
                        <Grid item md={4}>
                          {/* FACTORY PACKAGING COUNT */}
                          <FormControl fullWidth>
                            <CustomFormLabel htmlFor="factory_package">
                              {t('modals.form.ui.factory-package')}
                            </CustomFormLabel>
                            <RHFIntegerTextField
                              allowNegative={false}
                              fullWidth
                              name="factory_package"
                              id="factory_package"
                              variant="filled"
                            />
                          </FormControl>
                          {/* FACTORY PACKAGING COUNT END */}
                        </Grid>
                        <Grid item md={4}>
                          {/* BLISTER COUNT */}
                          <FormControl fullWidth>
                            <CustomFormLabel htmlFor="blister_count">
                              {t('modals.form.ui.blister-count')}
                            </CustomFormLabel>
                            <RHFIntegerTextField fullWidth name="blister_count" id="blister_count" variant="filled" />
                          </FormControl>
                          {/* BLISTER COUNT END */}
                        </Grid>
                      </Grid>
                      <Grid item md={8}>
                        {/* MAKER */}
                        <FormControl fullWidth>
                          <CustomFormLabel isRequired htmlFor="maker_id">
                            {t('modals.form.maker')}
                          </CustomFormLabel>
                          <RHFMakerField
                            fullWidth
                            name="maker"
                            id="maker"
                            TextFieldProps={{ variant: 'filled' }}
                            slotProps={{ popper: { style: { width: 'fit-content' } } }}
                          />
                        </FormControl>
                        {/* MAKER END */}
                      </Grid>
                      <Grid item md={8}>
                        {/* LICENSE HOLDER */}
                        <FormControl fullWidth>
                          <CustomFormLabel isRequired htmlFor="licenseHolder">
                            {t('modals.form.license-holder')}
                          </CustomFormLabel>
                          <RHFMakerField
                            fullWidth
                            name="licenseHolder"
                            id="licenseHolder"
                            TextFieldProps={{ variant: 'filled' }}
                            slotProps={{ popper: { style: { width: 'fit-content' } } }}
                          />
                        </FormControl>
                        {/* LICENSE HOLDER END */}
                      </Grid>
                    </Grid>
                    <Grid container rowSpacing={2}>
                      <Grid item md={12}>
                        <Typography component="p" variant="Title/Bold/medium">
                          {t('modals.form.sections.additional-info')}
                        </Typography>
                      </Grid>
                      <Grid container item md={12} spacing={2}>
                        <Grid item md={4}>
                          {/* MEASURE UNIT */}
                          <FormControl fullWidth>
                            <CustomFormLabel htmlFor="measureUnit">{t('modals.form.measure-unit')}</CustomFormLabel>
                            <RHFAutocomplete
                              fullWidth
                              name="measureUnit"
                              id="measureUnit"
                              loading={isFetchingMU}
                              loadingText={t('modals.form.ui.loading')}
                              noOptionsText={t('modals.form.ui.no-data')}
                              options={measureUnits}
                              getOptionLabel={option => (typeof option === 'string' ? option : `${option.name}`)}
                              renderOption={(props, option) => (
                                <li {...props}>{`${option.name} - ${option.description}`}</li>
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              TextFieldProps={{ variant: 'filled', placeholder: t('modals.form.ui.search') }}
                            />
                          </FormControl>
                          {/* MEASURE UNIT END */}
                        </Grid>
                        <Grid item md={4}>
                          {/* RELEASE FORM */}
                          <FormControl fullWidth>
                            <CustomFormLabel isRequired={isSelectedCategoryOfTypeMedicines} htmlFor="releaseForm">
                              {t('modals.form.form')}
                            </CustomFormLabel>
                            <RHFClassificationField
                              fullWidth
                              parentId={CLASSIFICATION_FORM_NODE_ID}
                              name="releaseForm"
                              id="releaseForm"
                              variant="filled"
                              errorMessage={t('modals.form.validation.required')}
                              isParentSelectable={false}
                              shouldShowSelectedValueDescription={false}
                              alias={CLASSIFICATION_FORM}
                            />
                          </FormControl>
                          {/* RELEASE FORM END */}
                        </Grid>
                        <Grid item md={4}>
                          {/* IS RECEIPT  */}
                          <FormControl fullWidth>
                            <RHFCheckbox
                              label={t('modals.form.ui.prescription-dispensing')}
                              name="is_receipt"
                              color="secondary"
                              sx={{ marginTop: 2.5, width: '100%' }}
                            />
                          </FormControl>
                          {/* IS RECEIPT END */}
                        </Grid>
                      </Grid>
                      <Grid container item md={12}>
                        <Grid item md={8}>
                          {/* INN */}
                          <FormControl fullWidth>
                            <CustomFormLabel isRequired={isSelectedCategoryOfTypeMedicines} htmlFor="inn">
                              {t('modals.form.inn')}
                            </CustomFormLabel>
                            <RHFInnField fullWidth name="inn" id="inn" TextFieldProps={{ variant: 'filled' }} />
                          </FormControl>
                          {/* INN END */}
                        </Grid>
                      </Grid>
                      <Grid container item md={12}>
                        <Grid item md={8}>
                          {/* CLASSIFICATION */}
                          <FormControl fullWidth>
                            <CustomFormLabel isRequired={isSelectedCategoryOfTypeMedicines} htmlFor="classification">
                              {t('modals.form.atx-classification')}
                            </CustomFormLabel>
                            <RHFClassificationField
                              fullWidth
                              parentId={CLASSIFICATION_ATC_NODE_ID}
                              name="classification"
                              id="classification"
                              variant="filled"
                              alias={CLASSIFICATION_ATC}
                            />
                          </FormControl>
                          {/* CLASSIFICATION END */}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container rowSpacing={2}>
                      <Grid item md={12}>
                        <Typography component="p" variant="Title/Bold/medium">
                          {t('modals.form.sections.commercial-data')}
                        </Typography>
                      </Grid>
                      <Grid container item md={12} spacing={2}>
                        <Grid item md={4}>
                          {/* IKPU */}
                          <FormControl fullWidth sx={{ flex: 6 }}>
                            <CustomFormLabel htmlFor="ikpu">{t('modals.form.ikpu-code')}</CustomFormLabel>
                            <RHFIkpuField
                              fullWidth
                              name="ikpu"
                              id="ikpu"
                              TextFieldProps={{
                                variant: 'filled',
                                placeholder: t('modals.form.ui.search-by-name-barcode-ikpu'),
                              }}
                            />
                          </FormControl>
                          {/* IKPU END */}
                        </Grid>
                        <Grid item md={4}>
                          {/* PACKAGE CODE */}
                          <FormControl fullWidth sx={{ flex: 6 }}>
                            <CustomFormLabel htmlFor="package_code">{t('modals.form.package-code')}</CustomFormLabel>
                            <RHFTextField disabled fullWidth name="package_code" id="package_code" variant="filled" />
                          </FormControl>
                          {/* PACKAGE CODE END */}
                        </Grid>
                        <Grid item md={4}>
                          {/* HAS MARKS  */}
                          <FormControl sx={{ flex: 6 }}>
                            <RHFCheckbox
                              label={t('modals.form.ui.marking-sign')}
                              name="has_marks"
                              color="secondary"
                              sx={{ marginTop: 2.5, width: '100%' }}
                              disabled
                            />
                          </FormControl>
                          {/* HAS MARKS END */}
                        </Grid>
                      </Grid>
                      <Grid container item md={12} spacing={2}>
                        <Grid item md={4}>
                          {/* VAT */}
                          <FormControl fullWidth sx={{ flex: 3 }}>
                            <CustomFormLabel htmlFor="vat_id">{t('modals.form.vat-rate')}</CustomFormLabel>

                            {isLoadingVats && (
                              <Skeleton variant="text" width="100%" height={48} sx={{ transform: 'none' }} />
                            )}
                            {!isLoadingVats && (
                              <RHFTextField select disabled fullWidth name="vat_id" id="vat_id" variant="filled">
                                {vats.map(vat => (
                                  <MenuItem key={vat.id} value={String(vat.id)}>
                                    {vat.name}%
                                  </MenuItem>
                                ))}
                              </RHFTextField>
                            )}
                          </FormControl>
                          {/* VAT END */}
                        </Grid>
                      </Grid>
                      <Grid container item md={12} spacing={2}>
                        <Grid item md={4}>
                          {/* BARCODES */}
                          <NomenclatureFormBarcodes />
                          {/* BARCODES END */}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Stack>
                </Form>
              </ModalBodyContent>
            </ModalBody>

            <ModalSidebarContent>
              <NomenclatureModalSidebar
                isEditMode={isEditMode || isEditAfterCreationMode}
                drug={drug}
                onCancel={onClose}
                formId={formId}
              />
            </ModalSidebarContent>
          </Stack>
        </ModalBodyContainer>
      </FormProvider>

      {!isEditMode && (
        <NomenclatureConfirmationCreateDialog
          isLoading={isSubmitting}
          onProceed={wrapHandleSubmit(handleSubmit, handleValidationError)}
        />
      )}

      {!isCreateMode && (
        <NomenclatureConfirmationEditDialog
          isLoading={isSubmitting}
          onProceed={wrapHandleSubmit(handleSubmit, handleValidationError)}
          onClose={handleNomenclatureConfirmationEditDialogClose}
        />
      )}
    </React.Fragment>
  );
};
